import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab, useTheme } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { VideoType } from '@tmdb-graphql-api/schema/types';
import { type SyntheticEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FeaturePlayerTabPanel } from '#components/PlayerSelectorTabs/FeaturePlayerTabPanel/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { type ScreenFormatType } from '#enums/mediaTypes.ts';
import { type PlayerTypeLowerCase } from '#types.ts';
import { TrailerPlayerTabPanel } from './TrailerPlayerTabPanel.tsx';

export type PlayerSelectorTabsProps = {
  episodeNumber?: number;
  id: string;
  seasonNumber?: number;
  title?: string;
  type: ScreenFormatType;
};

export const PlayerSelectorTabs = ({ episodeNumber, id, seasonNumber, title, type }: PlayerSelectorTabsProps) => {
  const theme = useTheme();
  const feature = useFeature(FeatureFlags.ExoticPinkPtarmigan);
  const [player, setPlayer] = useState<PlayerTypeLowerCase>('trailer');
  const tabPanelRef = useRef<HTMLDivElement>(undefined);
  const [tabPanelHeight, setTabPanelHeight] = useState<string>();

  useLayoutEffect(() => {
    if (tabPanelRef.current) {
      const { width } = tabPanelRef.current.getBoundingClientRect();
      const { clientWidth: screenWidth } = document.body;
      let multiplier = 0.75;

      if (screenWidth >= 600 && screenWidth < 900) {
        multiplier = 0.56;
      } else if (screenWidth >= 900) {
        multiplier = 0.4;
      }

      setTabPanelHeight(`${String(Math.round(width * multiplier))}px`);
    }
    // We want this to re-execute when the value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabPanelRef.current]);

  const players = {
    feature: (
      <FeaturePlayerTabPanel
        episodeNumber={episodeNumber}
        id={id}
        seasonNumber={seasonNumber}
        title={title}
        type={type}
      />
    ),
    trailer: <TrailerPlayerTabPanel id={id} type={type} videoTypes={[VideoType.Trailer, VideoType.Clip]} />,
  };

  const onChange = useCallback<(event: SyntheticEvent, value: PlayerTypeLowerCase) => void>(
    (_event, value) => {
      setPlayer(value);
    },
    [setPlayer],
  );

  const tabSx = {
    color: theme.supportingTextSecondary,
  };

  return (
    <TabContext value={player}>
      <TabList aria-label="Video player types" onChange={onChange}>
        <Tab
          disableRipple={!feature}
          label="Trailer"
          sx={{ ...tabSx, cursor: feature ? undefined : 'default' }}
          value="trailer"
        />
        {feature ? <Tab label="Feature" sx={tabSx} value="feature" /> : undefined}
      </TabList>
      <TabPanel
        ref={tabPanelRef}
        sx={{ minHeight: tabPanelHeight ?? '100px', padding: 0, position: 'relative' }}
        value={player}
      >
        {players[player]}
      </TabPanel>
    </TabContext>
  );
};
